/**
 * PerChildContractorsReview · iter303ak-fix24 — Accepted · T30 · Action 20
 * ───────────────────────────────────────────────────────────────────────
 * Per-child accordion for reviewing the Contractors assignments. One row
 * per accepted child. Each row:
 *   • fetches /appraisals/{child_id}/contractor-overview (summary of
 *     contractor types, assigned people, per-line overrides)
 *   • "Open Contractors tab" popup → /appraisals/{child_id}?embed=contractors
 *   • "Reviewed & ready" checkbox
 * Continue disabled until all rows ticked.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { CheckCircle2, ExternalLink, Users, AlertTriangle } from 'lucide-react';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

export default function PerChildContractorsReview({ action, capturedData, onNext, onBack, onCancel, canBack, value, onChange }) {
  const params = action.params || {};

  const acceptedChildren = useMemo(() => {
    const decisions = capturedData?.decisions
      || capturedData?.accepted_variants_pick?.decisions
      || capturedData?.await_quote_response?.decisions
      || capturedData?.appraisal?.child_decisions
      || capturedData?.appraisal_record?.child_decisions
      || {};
    return Object.entries(decisions)
      .filter(([, d]) => d?.decision === 'accepted')
      .map(([child_id, d]) => ({ child_id, ...d }));
  }, [capturedData]);

  const [overviews, setOverviews] = useState({}); // child_id -> overview
  const [reviewedIds, setReviewedIds] = useState(() => new Set(value?.reviewed_ids || []));
  const [loading, setLoading] = useState(true);
  const [openWindow, setOpenWindow] = useState({}); // child_id -> window ref

  const loadOverviews = useCallback(async () => {
    if (!acceptedChildren.length) { setLoading(false); return; }
    setLoading(true);
    try {
      const results = {};
      for (const c of acceptedChildren) {
        try {
          const { data } = await api.get(`/appraisals/${c.child_id}/contractor-overview`);
          results[c.child_id] = data;
        } catch (e) {
          results[c.child_id] = { error: e?.response?.data?.detail || 'Failed to load contractor overview' };
        }
      }
      setOverviews(results);
    } finally { setLoading(false); }
  }, [acceptedChildren]);

  useEffect(() => { loadOverviews(); }, [loadOverviews]);

  // Poll for popup close → refresh that child's overview.
  useEffect(() => {
    const timers = [];
    for (const [cid, win] of Object.entries(openWindow)) {
      if (!win) continue;
      const timer = setInterval(async () => {
        if (win.closed) {
          clearInterval(timer);
          setOpenWindow(prev => ({ ...prev, [cid]: null }));
          try {
            const { data } = await api.get(`/appraisals/${cid}/contractor-overview`);
            setOverviews(prev => ({ ...prev, [cid]: data }));
          } catch { /* silent */ }
        }
      }, 500);
      timers.push(timer);
    }
    return () => { timers.forEach(clearInterval); };
  }, [openWindow]);

  const openChildContractorsTab = (child_id) => {
    const url = `/appraisals/${child_id}?embed=contractors`;
    const win = window.open(url, `child_contractors_${child_id}`, 'width=1400,height=900');
    if (!win) { alert('Popup blocked — allow popups for this site and try again.'); return; }
    setOpenWindow(prev => ({ ...prev, [child_id]: win }));
  };

  const toggleReviewed = (child_id) => {
    setReviewedIds(prev => {
      const next = new Set(prev);
      if (next.has(child_id)) next.delete(child_id); else next.add(child_id);
      return next;
    });
  };

  useEffect(() => {
    if (onChange) {
      onChange({
        reviewed_ids: Array.from(reviewedIds),
        overviews_snapshot: overviews,
      });
    }
  }, [reviewedIds, overviews, onChange]);

  const allReviewed = acceptedChildren.length > 0 && acceptedChildren.every(c => reviewedIds.has(c.child_id));

  const advance = () => { if (allReviewed) onNext(); };

  const _summariseOverview = (o) => {
    if (!o || o.error) return { types: 0, contractors: 0, lines: 0 };
    const by_type = o.by_type || {};
    const typesCount = Object.keys(by_type).length;
    const contractorIds = new Set();
    let linesCount = 0;
    for (const t of Object.values(by_type)) {
      linesCount += (t.by_line || []).length;
      for (const ln of (t.by_line || [])) {
        if (ln.contractor_id) contractorIds.add(ln.contractor_id);
      }
    }
    if (Array.isArray(o.by_line)) {
      linesCount = o.by_line.length;
      for (const ln of o.by_line) {
        if (ln.contractor_id) contractorIds.add(ln.contractor_id);
      }
    }
    return { types: typesCount, contractors: contractorIds.size, lines: linesCount };
  };

  return (
    <div className="space-y-4" data-testid="wf-per-child-contractors-review">
      <div className="rounded-xl border border-purple-200 bg-purple-50/60 p-4">
        <div className="flex items-start gap-3">
          <Users className="w-5 h-5 mt-0.5 shrink-0 text-purple-700" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-purple-900">Review Contractors — per accepted quote</div>
            <div className="text-sm mt-1 text-purple-800">
              {params.prompt || 'Review contractor assignments and rate overrides for each accepted quote. Open the Contractors tab to make changes, then tick each row as ready.'}
            </div>
          </div>
        </div>
      </div>

      {loading && <div className="p-6 text-sm text-slate-400 italic">Loading contractor overviews…</div>}

      {!loading && acceptedChildren.length === 0 && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
          No accepted quotes found. Go back and confirm at least one.
        </div>
      )}

      {!loading && acceptedChildren.length > 0 && (
        <div className="space-y-2">
          {acceptedChildren.map((c) => {
            const o = overviews[c.child_id] || {};
            const reviewed = reviewedIds.has(c.child_id);
            const label = [c.quote_type_ref, c.variant_label].filter(Boolean).join(' · ');
            const s = _summariseOverview(o);
            const hasError = !!o.error;
            return (
              <div key={c.child_id} className={`p-3 border rounded-lg ${reviewed ? 'bg-purple-50/40 border-purple-300' : 'bg-white border-slate-200'}`} data-testid={`wf-per-child-contractors-row-${c.child_id}`}>
                <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-sm font-medium text-slate-800">{label || 'Quote'}</div>
                    {c.appraisal_no && (
                      <span className="text-[10px] font-bold uppercase tracking-widest text-indigo-800 bg-indigo-100 border border-indigo-300 rounded px-1.5 py-0.5">
                        A{c.appraisal_no}
                      </span>
                    )}
                    {!hasError && (
                      <>
                        <span className="text-[10px] font-semibold text-slate-500 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5">
                          {s.types} type{s.types === 1 ? '' : 's'}
                        </span>
                        <span className="text-[10px] font-semibold text-slate-500 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5">
                          {s.contractors} contractor{s.contractors === 1 ? '' : 's'} assigned
                        </span>
                        <span className="text-[10px] font-semibold text-slate-500 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5">
                          {s.lines} line{s.lines === 1 ? '' : 's'}
                        </span>
                      </>
                    )}
                    {hasError && (
                      <span className="text-[10px] font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 inline-flex items-center gap-1">
                        <AlertTriangle size={10} /> {o.error}
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => openChildContractorsTab(c.child_id)}
                    className="px-3 py-1.5 rounded-md text-xs font-semibold text-purple-700 bg-white border border-purple-300 hover:bg-purple-50 inline-flex items-center gap-1.5"
                    data-testid={`wf-per-child-contractors-open-${c.child_id}`}
                  >
                    <ExternalLink size={12} /> Open Contractors tab
                  </button>
                </div>

                <label className="flex items-center gap-2 pt-2 border-t border-slate-100 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={reviewed}
                    onChange={() => toggleReviewed(c.child_id)}
                    className="rounded"
                    data-testid={`wf-per-child-contractors-reviewed-${c.child_id}`}
                  />
                  <span className={`text-xs font-medium ${reviewed ? 'text-purple-800' : 'text-slate-600'}`}>
                    Reviewed &amp; ready
                  </span>
                </label>
              </div>
            );
          })}
        </div>
      )}

      <div className="flex items-center justify-between gap-3 pt-2">
        {canBack ? (
          <button onClick={onBack} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="wf-per-child-contractors-back">← Back</button>
        ) : <span />}
        <div className="flex gap-2">
          {onCancel && (
            <button onClick={onCancel} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="wf-per-child-contractors-cancel">Cancel</button>
          )}
          <button
            onClick={advance}
            disabled={!allReviewed}
            className="px-4 py-1.5 rounded-md text-sm bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-40 inline-flex items-center gap-2"
            data-testid="wf-per-child-contractors-continue"
          >
            <CheckCircle2 className="w-4 h-4" /> Continue
          </button>
        </div>
      </div>
    </div>
  );
}
